import React from "react";
import { useAgreementStatus } from "@/hooks/useAgreementStatus";
import LegalAgreementModal from "@/components/LegalAgreementModal";

interface Props {
  slug: string;
  children: React.ReactNode;
}

/**
 * Wraps any content behind a legal agreement acceptance check.
 * If the user has not accepted (or must re-accept), a full-screen blocking modal is shown.
 * Once accepted, the children are rendered normally.
 *
 * Usage:
 *   <AgreementGate slug="deployment-compliance">
 *     <MyPanel />
 *   </AgreementGate>
 */
export default function AgreementGate({ slug, children }: Props) {
  const { needsAcceptance, isLoading, agreement, refetch } =
    useAgreementStatus(slug);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (needsAcceptance && agreement) {
    return <LegalAgreementModal agreement={agreement} onAccepted={refetch} />;
  }

  // Agreement is accepted (or not found — fail open so content isn't permanently blocked)
  return <>{children}</>;
}
